/**
 * The UI-language preference: the closed set of supported locales, and the pure fallback chain
 * that resolves what a viewer actually sees.
 *
 * `BACKLOG.md` B7. The resolution order: an explicit account preference wins outright; failing
 * that, the first supported entry in the browser's own `navigator.languages` (matched on base
 * subtag, so `es-MX` resolves to `es`); failing that, English. No
 * `i18next-browser-languagedetector` or other library layered on top — that would run its own
 * independent querystring/cookie/localStorage/navigator detection order, competing with this one
 * instead of composing with it. One pure, tested function is what `i18n/index.ts` calls directly.
 *
 * **Deliberately small.** Ships only Spanish, matching the actual request — the fallback chain and
 * every downstream mechanism (the parity test, the locale-select UI) read `SUPPORTED_LOCALES`
 * rather than a hardcoded pair, so adding a third locale later is a one-line change here plus a new
 * `web/src/locales/<code>/` directory, not a redesign.
 */

export const SUPPORTED_LOCALES = ['en', 'es'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * What a viewer actually sees, given their stored account preference (`null` when unset — a user
 * has never chosen one, or explicitly cleared back to "browser default") and the browser's own
 * `navigator.languages` (most-preferred first).
 *
 * A browser tag is matched on its base subtag only (`es-MX` → `es`), since this app has no
 * region-specific content to distinguish `es-MX` from `es-ES`.
 */
export function resolveDisplayLocale(
  accountLocale: Locale | null,
  browserLocales: readonly string[],
): Locale {
  if (accountLocale !== null) return accountLocale;

  for (const tag of browserLocales) {
    const base = tag.split('-')[0]?.toLowerCase();
    if (base !== undefined && isLocale(base)) return base;
  }

  return DEFAULT_LOCALE;
}
